import React from 'react'
import { Link } from 'react-router-dom'
import SeoHead from '../components/SeoHead'
import { COMPANY, SERVICES, HOME_FAQ, IMAGES } from '../data/siteData'
import { PAGE_META } from '../data/metaData'
import { getOptimizedImageUrl } from '../lib/imageHelper'
import ContactForm from '../components/ContactForm'
import GallerySection from '../components/GallerySection'
import FaqAccordion from '../components/FaqAccordion'
import CtaBanner from '../components/CtaBanner'

export default function HomePage() {
  const metaTitle = PAGE_META.home.title
  const metaDescription = PAGE_META.home.description

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: HOME_FAQ.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  }

  return (
    <div>
      <SeoHead
        title={metaTitle}
        description={metaDescription}
        schemaData={faqSchema}
        canonicalPath="/"
      />

      {/* Hero Section */}
      <section className="relative bg-stone-950 text-white overflow-hidden py-16 sm:py-24 lg:py-28 border-b border-stone-800">
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl(IMAGES.hero, { width: 1800, quality: 80 })}
            alt="JT Knudsen minigraver i arbejde med udgravning og haveanlæg"
            className="w-full h-full object-cover object-center opacity-65 brightness-95"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/80 via-stone-950/50 to-stone-950/20"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#015205]/40 border border-[#015205]/80 text-emerald-300 text-xs sm:text-sm font-semibold mb-6">
              <i className="fa-solid fa-hard-hat" aria-hidden="true"></i>
              <span>Lokal anlægsvirksomhed i Hjortshøj</span>
            </div>

            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight drop-shadow-md">
              Anlægsentreprenør i Hjortshøj
            </h1>

            <p className="mt-5 text-base sm:text-xl text-stone-200 leading-relaxed max-w-2xl drop-shadow-sm">
              Hos JT Knudsen ved Jonas Knudsen får du hjælp fra en lokal virksomhed, der løser bygge og anlægsarbejde med blik for både funktion og det færdige udtryk. Vi udfører opgaver i Hjortshøj, resten af Østjylland og på Djursland med udgangspunkt i dine ønsker og forholdene på stedet.
            </p>

            {/* CTAs */}
            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-lg bg-[#015205] hover:bg-[#026b07] active:bg-[#014204] text-white font-bold text-base shadow-lg shadow-[#015205]/30 transition-all cursor-pointer whitespace-nowrap"
              >
                <span>Få et uforpligtende tilbud</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
              </Link>

              <a
                href={COMPANY.phoneHref}
                className="inline-flex items-center justify-center gap-2.5 px-6 py-4 rounded-lg bg-stone-900 hover:bg-stone-800 text-stone-100 font-bold text-base border border-stone-700 transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-emerald-400" aria-hidden="true"></i>
                <span>Ring på {COMPANY.phone}</span>
              </a>
            </div>

            {/* Quick highlights */}
            <div className="mt-10 pt-8 border-t border-stone-800/80 grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs sm:text-sm text-stone-300">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-user-check text-emerald-400" aria-hidden="true"></i>
                <span>Direkte kontakt til Jonas</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-truck-pickup text-emerald-400" aria-hidden="true"></i>
                <span>Moderne maskiner</span>
              </div>
              <div className="flex items-center gap-2 col-span-2 sm:col-span-1">
                <i className="fa-solid fa-shield-check text-emerald-400" aria-hidden="true"></i>
                <span>Ordentligt forarbejde</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Context Section */}
      <section className="py-14 sm:py-20 bg-stone-50 text-stone-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-5">
              <span className="text-xs font-bold uppercase tracking-wider text-[#015205]">
                Faglighed & Lokalt Nærvær
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-stone-900">
                Hvad kan din anlægsentreprenør i Hjortshøj hjælpe med?
              </h2>
              <div className="text-stone-700 space-y-4 text-base sm:text-lg leading-relaxed">
                <p>
                  Som anlægsentreprenør i Hjortshøj tager vi fat på både klassiske anlægsopgaver og arbejde, der kræver en mere specialiseret løsning. Det kan være renovering, en tilbygning eller praktiske forbedringer omkring boligen, hvor grundarbejde og udførelse skal spille sammen.
                </p>
                <p>
                  En god løsning begynder med, at opgaven bliver afklaret ordentligt. Vi taler derfor om dine ønsker, pladsen på grunden, adgangsforholdene og det resultat, du vil opnå. På den måde kan arbejdet planlægges ud fra de faktiske forhold frem for en standardløsning.
                </p>
              </div>

              <div className="pt-2 flex flex-wrap items-center gap-3">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#015205] hover:bg-[#026b07] text-white font-semibold text-sm transition-colors cursor-pointer shadow-sm"
                >
                  <span>Kontakt Jonas om din opgave</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </Link>
                <Link
                  to="/ydelser"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-stone-200 hover:bg-stone-300 text-stone-900 font-semibold text-sm transition-colors cursor-pointer"
                >
                  <span>Se alle ydelser</span>
                  <i className="fa-solid fa-arrow-right text-xs text-[#015205]" aria-hidden="true"></i>
                </Link>
              </div>

              {/* Hurtig adgang til undersider under ydelser */}
              <div className="pt-3 border-t border-stone-200">
                <div className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-2.5">
                  Gå direkte til ydelser:
                </div>
                <div className="flex flex-wrap gap-2">
                  <Link
                    to="/belaegning"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white hover:bg-[#015205] text-stone-800 hover:text-white border border-stone-300 hover:border-[#015205] text-xs font-medium transition-colors shadow-2xs"
                  >
                    <i className="fa-solid fa-shapes text-[#015205] text-[11px]" aria-hidden="true"></i>
                    <span>Belægning</span>
                  </Link>
                  <Link
                    to="/fundament"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white hover:bg-[#015205] text-stone-800 hover:text-white border border-stone-300 hover:border-[#015205] text-xs font-medium transition-colors shadow-2xs"
                  >
                    <i className="fa-solid fa-cubes-stacked text-[#015205] text-[11px]" aria-hidden="true"></i>
                    <span>Fundament & sokkel</span>
                  </Link>
                  <Link
                    to="/gravearbejde"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white hover:bg-[#015205] text-stone-800 hover:text-white border border-stone-300 hover:border-[#015205] text-xs font-medium transition-colors shadow-2xs"
                  >
                    <i className="fa-solid fa-trowel-bricks text-[#015205] text-[11px]" aria-hidden="true"></i>
                    <span>Gravearbejde</span>
                  </Link>
                  <Link
                    to="/nedgravning-af-trampolin"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white hover:bg-[#015205] text-stone-800 hover:text-white border border-stone-300 hover:border-[#015205] text-xs font-medium transition-colors shadow-2xs"
                  >
                    <i className="fa-solid fa-circle-nodes text-[#015205] text-[11px]" aria-hidden="true"></i>
                    <span>Nedgravning af trampolin</span>
                  </Link>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden shadow-xl border border-stone-200">
                <img
                  src="/cdn-cgi/image/width=800,height=600,fit=cover,quality=80,format=auto/_assets/udgravning-af-rende-med-minigraver-graesarmering.jpeg"
                  alt="Kompakt minigraver ved udgravning og anlægsarbejde i have"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-stone-950 via-stone-950/80 to-transparent p-5 text-white">
                  <div className="font-bold text-lg">Professionelt maskinarbejde</div>
                  <div className="text-xs text-emerald-300">
                    Kompakt materiel med fokus på præcision og skånsomhed
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Services Presentation */}
      <section className="py-16 sm:py-24 bg-stone-100/70 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-xs font-bold uppercase tracking-wider text-[#015205]">
              Vores kerneområder
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight mt-1.5">
              Få de vigtigste anlægsopgaver løst med omtanke
            </h2>
            <p className="mt-3 text-base sm:text-lg text-stone-600 leading-relaxed">
              Vores kerneområder spænder fra belægning og fundament til gravearbejde og nedgravning af trampolin ved hus og have. Fælles for opgaverne er, at forarbejdet har stor betydning for resultatet, og at materialer, mål og underlag skal passe til den konkrete anvendelse. Få et samlet overblik på vores side med{' '}
              <Link to="/ydelser" className="text-[#015205] font-semibold underline decoration-[#015205]/40 hover:decoration-[#015205] underline-offset-2 transition-colors">
                alle ydelser
              </Link>.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Service 1: Belægning */}
            <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm overflow-hidden flex flex-col group hover:shadow-md transition-shadow">
              <Link to="/belaegning" className="block relative aspect-16/9 overflow-hidden bg-stone-800">
                <img
                  src={getOptimizedImageUrl(IMAGES.belaegning.hero, { width: 700, height: 400 })}
                  alt="Nylagt flisebelægning i forhave ved murstenshus"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <span className="absolute top-4 left-4 bg-stone-900 text-stone-100 font-bold text-xs uppercase px-3 py-1 rounded-full shadow">
                  Flotte uderum
                </span>
              </Link>
              <div className="p-6 sm:p-8 flex-1 flex flex-col justify-between">
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-stone-900 mb-3">
                    <Link to="/belaegning" className="hover:text-[#015205] transition-colors">
                      Belægning der gør hverdagen lettere
                    </Link>
                  </h3>
                  <p className="text-sm sm:text-base text-stone-600 leading-relaxed mb-4">
                    Belægning skal både kunne bruges i hverdagen og passe til omgivelserne. Vi udfører belægning af indkørsel, terrasse og arealer i haven med fokus på bærelag, afretning og fald. Den rigtige opbygning er afgørende, når fliser og sten skal ligge pænt og stabilt.
                  </p>
                </div>
                <div className="pt-4 border-t border-stone-100">
                  <Link
                    to="/belaegning"
                    className="inline-flex items-center gap-2 font-bold text-sm text-[#015205] hover:text-[#026b07] transition-colors"
                  >
                    <span>Læs mere om belægning</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                  </Link>
                </div>
              </div>
            </div>

            {/* Service 2: Fundament */}
            <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm overflow-hidden flex flex-col group hover:shadow-md transition-shadow">
              <Link to="/fundament" className="block relative aspect-16/9 overflow-hidden bg-stone-800">
                <img
                  src={getOptimizedImageUrl(IMAGES.fundament.hero, { width: 700, height: 400 })}
                  alt="Udgravning til fundament og sokkel med gul CAT gravemaskine"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <span className="absolute top-4 left-4 bg-stone-900 text-stone-100 font-bold text-xs uppercase px-3 py-1 rounded-full shadow">
                  Præcisionsarbejde
                </span>
              </Link>
              <div className="p-6 sm:p-8 flex-1 flex flex-col justify-between">
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-stone-900 mb-3">
                    <Link to="/fundament" className="hover:text-[#015205] transition-colors">
                      Et solidt fundament begynder under jordniveau
                    </Link>
                  </h3>
                  <p className="text-sm sm:text-base text-stone-600 leading-relaxed mb-4">
                    Et fundament skal tilpasses konstruktionen, belastningen og forholdene i jorden. Vi hjælper med opbygning og støbning af fundament til byggeprojekter som tilbygninger og har fokus på præcis udgravning og et stabilt grundlag. Ved behov kan arbejdet også omfatte etablering af sokkel.
                  </p>
                </div>
                <div className="pt-4 border-t border-stone-100">
                  <Link
                    to="/fundament"
                    className="inline-flex items-center gap-2 font-bold text-sm text-[#015205] hover:text-[#026b07] transition-colors"
                  >
                    <span>Læs mere om fundament & sokkel</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                  </Link>
                </div>
              </div>
            </div>

            {/* Service 3: Gravearbejde */}
            <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm overflow-hidden flex flex-col group hover:shadow-md transition-shadow">
              <Link to="/gravearbejde" className="block relative aspect-16/9 overflow-hidden bg-stone-800">
                <img
                  src={getOptimizedImageUrl(IMAGES.gravearbejde.hero, { width: 700, height: 400 })}
                  alt="Minigraver ved udgravning og planering af jordbund"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <span className="absolute top-4 left-4 bg-stone-900 text-stone-100 font-bold text-xs uppercase px-3 py-1 rounded-full shadow">
                  Moderne maskiner
                </span>
              </Link>
              <div className="p-6 sm:p-8 flex-1 flex flex-col justify-between">
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-stone-900 mb-3">
                    <Link to="/gravearbejde" className="hover:text-[#015205] transition-colors">
                      Gravearbejde med moderne maskiner
                    </Link>
                  </h3>
                  <p className="text-sm sm:text-base text-stone-600 leading-relaxed mb-4">
                    Vi udfører alle former for mindre maskingravning, jordflytning og klargøring til have- og byggeprojekter. Med kompakte gummibånd og præcise skovle kan vi komme til på trange arealer i parcelhushaver uden unødige skader på omgivelserne.
                  </p>
                </div>
                <div className="pt-4 border-t border-stone-100">
                  <Link
                    to="/gravearbejde"
                    className="inline-flex items-center gap-2 font-bold text-sm text-[#015205] hover:text-[#026b07] transition-colors"
                  >
                    <span>Læs mere om gravearbejde</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                  </Link>
                </div>
              </div>
            </div>

            {/* Service 4: Trampolin */}
            <div className="bg-white rounded-2xl border border-stone-200/90 shadow-sm overflow-hidden flex flex-col group hover:shadow-md transition-shadow">
              <Link to="/nedgravning-af-trampolin" className="block relative aspect-16/9 overflow-hidden bg-stone-800">
                <img
                  src={getOptimizedImageUrl(IMAGES.trampolin.hero, { width: 700, height: 400 })}
                  alt="Professionelt nedgravet trampolin i plan med græsplænen i have"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <span className="absolute top-4 left-4 bg-stone-900 text-stone-100 font-bold text-xs uppercase px-3 py-1 rounded-full shadow">
                  Have & fritid
                </span>
              </Link>
              <div className="p-6 sm:p-8 flex-1 flex flex-col justify-between">
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-stone-900 mb-3">
                    <Link to="/nedgravning-af-trampolin" className="hover:text-[#015205] transition-colors">
                      Nedgravning af trampolin, hvad skal der tages højde for?
                    </Link>
                  </h3>
                  <p className="text-sm sm:text-base text-stone-600 leading-relaxed mb-4">
                    Vi udfører nedgravning af trampolin og tager højde for trampolinens mål, havens adgang og jordens karakter. Placering og dybde skal passe til den valgte trampolin, mens luft omkring konstruktionen og mulighed for afvanding også bør indgå i løsningen. Vi afklarer samtidig, om modellen er en trampolin til nedgravning.
                  </p>
                </div>
                <div className="pt-4 border-t border-stone-100">
                  <Link
                    to="/nedgravning-af-trampolin"
                    className="inline-flex items-center gap-2 font-bold text-sm text-[#015205] hover:text-[#026b07] transition-colors"
                  >
                    <span>Læs mere om nedgravning af trampolin</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Overordnet link til alle ydelser */}
          <div className="mt-12 bg-white rounded-2xl p-6 sm:p-8 border border-stone-200 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="space-y-1 text-center md:text-left">
              <h3 className="text-lg sm:text-xl font-bold text-stone-900">
                Se det samlede overblik over vores ydelser
              </h3>
              <p className="text-sm text-stone-600 max-w-xl">
                Få information om alle vores anlægs- og graveydelser til have, bolig og byggeri i Østjylland og på Djursland.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
              <Link
                to="/ydelser"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-stone-900 hover:bg-[#015205] text-white font-bold text-sm transition-colors shadow-sm"
              >
                <span>Gå til ydelser</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 sm:py-20 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-400">
              Enkel & Sikker Proces
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mt-1.5">
              Sådan skaber vi et overskueligt forløb
            </h2>
            <p className="mt-3 text-base sm:text-lg text-stone-300 leading-relaxed">
              Når du kontakter os om bygge og anlægsarbejde, begynder vi med at få styr på opgavens omfang og de praktiske forhold. Vi ser blandt andet på adgangen til arbejdsområdet, eksisterende overflader og behovet for udgravning, så du ved, hvad løsningen tager udgangspunkt i.
            </p>
            <p className="mt-2 text-sm sm:text-base text-stone-400">
              Under udførelsen holder vi fokus på den aftalte opgave og på, at de enkelte arbejdsgange kommer i den rigtige rækkefølge. Det giver et mere overskueligt forløb, uanset om du skal have klargjort et byggeareal med{' '}
              <Link to="/gravearbejde" className="text-emerald-400 font-semibold underline decoration-emerald-500/40 hover:decoration-emerald-400 underline-offset-2 transition-colors">
                gravearbejde
              </Link>, lagt ny{' '}
              <Link to="/belaegning" className="text-emerald-400 font-semibold underline decoration-emerald-500/40 hover:decoration-emerald-400 underline-offset-2 transition-colors">
                belægning
              </Link>, etableret et{' '}
              <Link to="/fundament" className="text-emerald-400 font-semibold underline decoration-emerald-500/40 hover:decoration-emerald-400 underline-offset-2 transition-colors">
                fundament & sokkel
              </Link>{' '}eller gravet en{' '}
              <Link to="/nedgravning-af-trampolin" className="text-emerald-400 font-semibold underline decoration-emerald-500/40 hover:decoration-emerald-400 underline-offset-2 transition-colors">
                trampolin ned
              </Link>.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-stone-950/70 border border-stone-800 rounded-xl p-6 relative">
              <div className="w-10 h-10 rounded-full bg-[#015205] text-white font-black flex items-center justify-center text-base mb-4 ring-2 ring-emerald-500/30">
                1
              </div>
              <h3 className="text-lg font-bold text-white mb-2">
                Afklaring & Besigtigelse
              </h3>
              <p className="text-sm text-stone-400 leading-relaxed">
                Vi taler om opgavens type, trampolinens eller byggeriets mål, adgangsforhold for minigraver og eventuelle særlige jordbundsforhold.
              </p>
            </div>

            <div className="bg-stone-950/70 border border-stone-800 rounded-xl p-6 relative">
              <div className="w-10 h-10 rounded-full bg-[#015205] text-white font-black flex items-center justify-center text-base mb-4 ring-2 ring-emerald-500/30">
                2
              </div>
              <h3 className="text-lg font-bold text-white mb-2">
                Pris & Planlægning
              </h3>
              <p className="text-sm text-stone-400 leading-relaxed">
                Du modtager et gennemskueligt og uforpligtende tilbud. Vi aftaler præcis startdato og hvordan overskudsjord skal håndteres eller bortkøres.
              </p>
            </div>

            <div className="bg-stone-950/70 border border-stone-800 rounded-xl p-6 relative">
              <div className="w-10 h-10 rounded-full bg-[#015205] text-white font-black flex items-center justify-center text-base mb-4 ring-2 ring-emerald-500/30">
                3
              </div>
              <h3 className="text-lg font-bold text-white mb-2">
                Udførelse & Oprydning
              </h3>
              <p className="text-sm text-stone-400 leading-relaxed">
                Jonas udfører grave- og anlægsarbejdet med professionelt materiel og efterlader haven eller byggepladsen pæn, ryddelig og klar til brug.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages / Why Choose JT Knudsen */}
      <section className="py-16 sm:py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-bold uppercase tracking-wider text-[#015205]">
              Værdier der tæller
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight mt-1.5">
              Hvorfor vælge JT Knudsen?
            </h2>
            <p className="mt-2 text-stone-600 text-sm sm:text-base">
              Når du hyrer JT Knudsen, handler du direkte med den fagmand, der sidder i maskinen og udfører arbejdet på din grund.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl border border-stone-200">
              <div className="w-12 h-12 rounded-lg bg-[#015205]/10 text-[#015205] flex items-center justify-center text-xl mb-4">
                <i className="fa-solid fa-comments" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-1.5">
                Direkte kontakt til Jonas
              </h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Ingen mellemled, ingen forsinkende kontorled. Du aftaler detaljerne direkte med den person, der løser opgaven.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl border border-stone-200">
              <div className="w-12 h-12 rounded-lg bg-[#015205]/10 text-[#015205] flex items-center justify-center text-xl mb-4">
                <i className="fa-solid fa-sliders" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-1.5">
                Fleksibel løsning af opgaver
              </h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Vi hjælper både med den enkelte <Link to="/nedgravning-af-trampolin" className="text-[#015205] font-semibold hover:underline">havetrampolin</Link> og større projekter med <Link to="/fundament" className="text-[#015205] font-semibold hover:underline">fundamenter & sokler</Link>, ny <Link to="/belaegning" className="text-[#015205] font-semibold hover:underline">belægning</Link> eller generelt <Link to="/gravearbejde" className="text-[#015205] font-semibold hover:underline">gravearbejde</Link>.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl border border-stone-200">
              <div className="w-12 h-12 rounded-lg bg-[#015205]/10 text-[#015205] flex items-center justify-center text-xl mb-4">
                <i className="fa-solid fa-gears" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-1.5">
                Moderne maskiner
              </h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Velholdt minigraver og udstyr, der er skånsomt mod haver og effektivt udfører selv præcise opgaver.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl border border-stone-200">
              <div className="w-12 h-12 rounded-lg bg-[#015205]/10 text-[#015205] flex items-center justify-center text-xl mb-4">
                <i className="fa-solid fa-ruler-combined" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-1.5">
                Fokus på ordentligt resultat
              </h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Korrekt dybde, bærelag og finish. Arbejdet gøres grundigt første gang, så det holder i mange år.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl border border-stone-200">
              <div className="w-12 h-12 rounded-lg bg-[#015205]/10 text-[#015205] flex items-center justify-center text-xl mb-4">
                <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-1.5">
                Lokal forankring
              </h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Hjemmebase i Hjortshøj. Vi kender det lokale område, jordforholdene og holder hvad vi lover.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl border border-stone-200">
              <div className="w-12 h-12 rounded-lg bg-[#015205]/10 text-[#015205] flex items-center justify-center text-xl mb-4">
                <i className="fa-solid fa-file-lines" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-1.5">
                Uforpligtende tilbud
              </h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Du får altid et gennemskueligt og uforpligtende tilbud på din opgave uden skjulte overraskelser.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Local coverage section */}
      <section className="py-14 bg-stone-100 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-wider text-[#015205]">
              Områder vi dækker
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight mt-1">
              Lokale anlægsentreprenører i Østjylland og på Djursland
            </h2>
            <div className="mt-3 text-stone-700 text-sm sm:text-base space-y-3 leading-relaxed">
              <p>
                Fra vores base i Hjortshøj kører vi ud til opgaver i både Østjylland og på Djursland. Vi arbejder blandt andet i <strong>Aarhus, Grenaa, Hornslet, Hinnerup og Egå</strong>. Den lokale placering gør det naturligt for os at hjælpe med byggeopgaver og anlægsarbejde på tværs af området – herunder <Link to="/belaegning" className="text-[#015205] font-semibold hover:underline">belægning</Link>, <Link to="/fundament" className="text-[#015205] font-semibold hover:underline">fundament</Link>, <Link to="/gravearbejde" className="text-[#015205] font-semibold hover:underline">gravearbejde</Link> og <Link to="/nedgravning-af-trampolin" className="text-[#015205] font-semibold hover:underline">nedgravning af trampolin</Link>. Se også hele vores oversigt over <Link to="/ydelser" className="text-[#015205] font-semibold hover:underline">ydelser</Link>.
              </p>
              <p>
                Har du et projekt uden for de nævnte byer, er du også velkommen til at tage kontakt og høre om mulighederne. Opgavens type, omfang og adgangsforhold har betydning for planlægningen, og derfor tager vi gerne en konkret dialog om, hvad du har brug for.
              </p>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              {COMPANY.serviceAreas.map((area) => (
                <span
                  key={area}
                  className="px-3 py-1 rounded-md bg-white border border-stone-300 text-stone-800 text-xs font-semibold"
                >
                  <i className="fa-solid fa-map-pin text-[#015205] mr-1.5" aria-hidden="true"></i>
                  {area}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Highlight Section */}
      <section className="py-16 sm:py-20 bg-stone-50 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <GallerySection
            title="Udvalgte projekter og udført arbejde"
            subtitle="Et udpluk af vores gravearbejde, trampolinnedgravning, terrasser og fundamentopgaver i området"
            limit={6}
            showLinkToFull={true}
          />
        </div>
      </section>

      {/* FAQ Accordion */}
      <FaqAccordion
        items={HOME_FAQ}
        title="Ofte stillede spørgsmål om bygge- og anlægsarbejde"
        subtitle="Få hurtige svar på overvejelser om trampolinnedgravning, fundament og belægning"
      />

      {/* Main Contact Section on Homepage */}
      <section className="py-16 sm:py-24 bg-stone-950 text-white border-t border-stone-800" id="kontakt">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 space-y-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#015205]/40 text-emerald-400 border border-[#015205]/60 text-xs font-bold uppercase tracking-wider">
                <i className="fa-solid fa-phone" aria-hidden="true"></i>
                <span>Direkte kontakt</span>
              </span>

              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                Klar til at få din opgave vurderet?
              </h2>

              <p className="text-stone-300 text-base sm:text-lg leading-relaxed">
                Fortæl os, om du søger anlægsentreprenører til en konkret opgave, eller om du endnu overvejer den bedste løsning. Jo mere vi ved om arealet, dine ønsker og den ønskede tidsramme, desto bedre kan vi tage stilling til arbejdet og udarbejde et tilbud.
              </p>

              <div className="p-6 rounded-xl bg-stone-900 border border-stone-800 space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#015205]/40 text-emerald-400 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-user text-base" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div className="text-xs text-stone-400 font-medium uppercase tracking-wider">
                      Fagmand & Indehaver
                    </div>
                    <div className="text-base font-bold text-white">
                      {COMPANY.name}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#015205]/40 text-emerald-400 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-phone text-base" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div className="text-xs text-stone-400 font-medium uppercase tracking-wider">
                      Telefon
                    </div>
                    <a
                      href={COMPANY.phoneHref}
                      className="text-lg font-bold text-emerald-400 hover:text-emerald-300 transition-colors whitespace-nowrap"
                    >
                      {COMPANY.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#015205]/40 text-emerald-400 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-envelope text-base" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div className="text-xs text-stone-400 font-medium uppercase tracking-wider">
                      E-mail
                    </div>
                    <a
                      href={`mailto:${COMPANY.email}`}
                      className="text-sm font-semibold text-stone-200 hover:text-emerald-400 transition-colors whitespace-nowrap break-keep"
                    >
                      {COMPANY.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#015205]/40 text-emerald-400 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-location-dot text-base" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div className="text-xs text-stone-400 font-medium uppercase tracking-wider">
                      Adresse & base
                    </div>
                    <div className="text-sm text-stone-300">
                      {COMPANY.address}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ContactForm defaultService="Belægning" />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
